/**
 * Reactivate Current Grade 7 Students
 * Finds graduated students who should be active and fixes their status
 *
 * USAGE: await window.reactivateGrade7()
 */

import { collection, getDocs, updateDoc, doc } from 'firebase/firestore';
import { db } from '../firebase.js';

async function reactivateGrade7() {
  console.log('🔄 Finding Grade 7 students to reactivate...\n');

  try {
    // Get schoolId
    let schoolId = null;
    const keys = Object.keys(localStorage);
    const contextKey = keys.find(k => k.startsWith('swan_school_context_'));

    if (contextKey) {
      const contextData = localStorage.getItem(contextKey);
      if (contextData) {
        schoolId = JSON.parse(contextData).schoolId;
      }
    }

    if (!schoolId) {
      console.error('No schoolId found');
      return;
    }

    // Get all students
    const studentsRef = collection(db, `schools/${schoolId}/students`);
    const snapshot = await getDocs(studentsRef);

    // Find Grade 7 students who are marked as graduated
    const grade7ToReactivate = [];
    snapshot.forEach(doc => {
      const student = { id: doc.id, ...doc.data() };
      // If they're in 7A, 7B, or 7C and marked as graduated, they need reactivation
      if (student.status === 'graduated' &&
          student.className &&
          (student.className === '7A' || student.className === '7B' || student.className === '7C')) {
        grade7ToReactivate.push(student);
      }
    });

    console.log(`Found ${grade7ToReactivate.length} Grade 7 students marked as graduated`);

    if (grade7ToReactivate.length === 0) {
      console.log('No Grade 7 students need reactivation');
      return { success: true, reactivated: 0 };
    }

    // Group by class
    const byClass = {};
    grade7ToReactivate.forEach(s => {
      if (!byClass[s.className]) byClass[s.className] = [];
      byClass[s.className].push(s);
    });

    console.log('\nGrade 7 students to reactivate:');
    Object.entries(byClass).forEach(([className, students]) => {
      console.log(`\n${className} (${students.length} students):`);
      students.forEach(s => {
        console.log(`  - ${s.firstName} ${s.surname}`);
      });
    });

    console.log('\n⚠️  This will change these students from "graduated" to "active"');
    console.log('To proceed, run: await window.reactivateGrade7Confirm()');

    // Store for confirmation
    window._grade7ToReactivate = { schoolId, students: grade7ToReactivate };

    return { success: true, found: grade7ToReactivate.length, pending: true };

  } catch (error) {
    console.error('❌ Error:', error);
    throw error;
  }
}

async function reactivateGrade7Confirm() {
  if (!window._grade7ToReactivate) {
    console.error('❌ No pending students. Run reactivateGrade7() first.');
    return;
  }

  const { schoolId, students } = window._grade7ToReactivate;

  console.log(`\n🔄 Reactivating ${students.length} Grade 7 students...`);

  for (const student of students) {
    console.log(`  Reactivating: ${student.firstName} ${student.surname} (${student.className})`);
    const studentRef = doc(db, `schools/${schoolId}/students`, student.id);
    await updateDoc(studentRef, {
      status: 'active'
    });
  }

  console.log('\n✅ Complete! Grade 7 students are now active.');
  delete window._grade7ToReactivate;

  return { success: true, reactivated: students.length };
}

export { reactivateGrade7, reactivateGrade7Confirm };